import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";


const ProductBundleList = () => {
  const navigate = useNavigate();

  const schoolId = localStorage.getItem("school_id");

  const [filters, setFilters] = useState({
    q: "",
    category_id: "",
    subcategory_id: "",
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    totalProducts: 0,
    totalStock: 0,
    totalCost: 0,
    totalSale: 0,
  });
  const [loading, setLoading] = useState(false);

  // ✅ Frontend pagination only
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const listURL = `/product/bundle/list`;
  const categoryURL = `/product/category/list`;
  const subcategoryURL = `/product/subcategory/list`;

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await api.get(categoryURL, { headers: { "X-School-Id": schoolId } });
      const data = res.data;
      setCategories(Array.isArray(data) ? data : data?.data ?? []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  // Fetch subcategories
  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    try {
      const res = await api.get(`${subcategoryURL}?category_id=${categoryId}`, {
        headers: { "X-School-Id": schoolId },
      });
      const data = res.data;
      setSubcategories(Array.isArray(data) ? data : data?.data ?? []);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
      setSubcategories([]);
    }
  };

  // Fetch all products once
  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await api.get(listURL, {
        params: {
          school_id: schoolId,
          q: filters.q || "",
          category_id: filters.category_id || "",
          subcategory_id: filters.subcategory_id || "",
        },
      });

      const json = res.data;
      if (json?.status === "success") {
        const data = json.data || [];
        setRows(data);

        // summary calculation (total for all)
        setSummary({
          totalProducts: json.total || data.length,
          totalStock: json.total_stock || 0,
          totalCost: json.total_cost || 0,
          totalSale: json.total_sale || 0,
        });

        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Error fetching bundle list:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchSubcategories(filters.category_id);
  }, [filters.category_id]);

  useEffect(() => {
    if (schoolId) fetchList();
  }, [schoolId]);

  // Pagination logic (client-side)
  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const visibleRows = rows.slice(startIndex, endIndex);

  // Flatten variants for export
  const flattenData = (data) => {
    let arr = [];
    data.forEach((p) => {
      p.variants?.forEach((v) => {
        arr.push({
          product_name: p.product_name,
          variant_name: v.variant_name,
          stock_qty: v.stock_qty,
          cost_price: v.cost_price,
          sale_price: v.sale_price,
        });
      });
    });
    return arr;
  };

  // EXPORT HANDLERS
  const exportCSV = () => {
    const ws = XLSX.utils.json_to_sheet(flattenData(rows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Product Report");
    XLSX.writeFile(wb, "product_report.csv");
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(flattenData(rows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Product Report");
    XLSX.writeFile(wb, "product_report.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Product Stock Report", 14, 16);
    doc.setFontSize(10);
    doc.text(`Total Products: ${summary.totalProducts}`, 14, 24);

    autoTable(doc, {
      head: [["Product", "Variant", "Stock", "Cost ₹", "Sale ₹"]],
      body: flattenData(rows).map((r) => [
        r.product_name,
        r.variant_name,
        r.stock_qty,
        r.cost_price,
        r.sale_price,
      ]),
      startY: 30,
      styles: { fontSize: 9, cellPadding: 2 },
      theme: "striped",
    });

    doc.save("product_report.pdf");
  };

  return (
    <div style={pageContainer}>
      {/* HEADER */}
      <header style={headerBox}>
        <h2 style={pageTitle}>📦 Product Stock Dashboard</h2>
        <div style={headerActions}>
          <button style={btnExport} onClick={exportCSV}>CSV</button>
          <button style={btnExport} onClick={exportExcel}>Excel</button>
          <button style={btnExport} onClick={exportPDF}>PDF</button>
        </div>
      </header>

      {/* SUMMARY */}
      <section style={summaryBox}>
        <SummaryCard title="Total Products" color="#1e3a8a" bg="#dbeafe" value={summary.totalProducts} />
        <SummaryCard title="Total Stock" color="#166534" bg="#dcfce7" value={summary.totalStock.toLocaleString()} />
        <SummaryCard title="Total Cost (₹)" color="#854d0e" bg="#fef9c3" value={summary.totalCost.toLocaleString()} />
        <SummaryCard title="Total Sale (₹)" color="#115e59" bg="#ccfbf1" value={summary.totalSale.toLocaleString()} />
      </section>

      {/* FILTERS */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          fetchList();
        }}
        style={filterBox}
      >
        <input
          name="q"
          placeholder="🔍 Search product / variant"
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          style={inputStyle}
        />
        <select
          name="category_id"
          value={filters.category_id}
          onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
          style={dropdown}
        >
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.category_name}
            </option>
          ))}
        </select>
        <select
          name="subcategory_id"
          value={filters.subcategory_id}
          onChange={(e) => setFilters({ ...filters, subcategory_id: e.target.value })}
          style={dropdown}
          disabled={!filters.category_id}
        >
          <option value="">All Subcategories</option>
          {subcategories.map((s) => (
            <option key={s.id} value={s.id}>
              {s.subcategory_name}
            </option>
          ))}
        </select>
        <button type="submit" style={btnFilter}>Filter</button>
      </form>

      {/* PRODUCT TABLES */}
      {loading ? (
        <p style={{ textAlign: "center", padding: 20 }}>Loading...</p>
      ) : visibleRows.length > 0 ? (
        visibleRows.map((p) => (
          <section key={p.product_id} style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, color: "#0f172a" }}>{p.product_name}</h3>
                <div style={{ color: "#475569", fontSize: 14 }}>
                  {p.category_name} › {p.subcategory_name} • Type: {p.product_type}
                </div>
              </div>
              <Link to={`/admin/product-bundle/view/${p.product_id}`} style={btnGhost}>
                View
              </Link>
              <button
                onClick={() =>
                  navigate("/admin/product-bundle/create", {
                    state: {
                      mode: "edit",
                      product_id: p.product_id,
                    },
                  })
                }
                style={{
                  padding: "8px 14px",
                  background: "#f59e0b",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Edit
              </button>
            </div>

            {p.variants?.length ? (
              <div style={{ marginTop: 14, overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={th}>Variant</th>
                      <th style={thCenter}>Stock</th>
                      <th style={thRight}>Cost (₹)</th>
                      <th style={thRight}>Sale (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.variants.map((v, i) => (
                      <tr key={v.variant_id} style={i % 2 ? rowAlt : rowDefault}>
                        <td style={td}>{v.variant_name}</td>
                        <td style={tdCenter}>{v.stock_qty}</td>
                        <td style={tdRight}>{v.cost_price}</td>
                        <td style={tdRight}>{v.sale_price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: "#6b7280", marginTop: 8 }}>No variants available.</p>
            )}
          </section>
        ))
      ) : (
        <p style={{ textAlign: "center", padding: 20, color: "#6b7280" }}>No data found.</p>
      )}

      {/* PAGINATION */}
      {rows.length > 0 && (
        <div style={paginationBox}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              ...btn,
              backgroundColor: currentPage === 1 ? "#e5e7eb" : "#0ea5e9",
              color: currentPage === 1 ? "#94a3b8" : "#fff",
            }}
          >
            Prev
          </button>
          <span style={pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              ...btn,
              backgroundColor: currentPage === totalPages ? "#e5e7eb" : "#0ea5e9",
              color: currentPage === totalPages ? "#94a3b8" : "#fff",
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ title, color, bg, value }) => (
  <div style={{ ...summaryCard, background: bg, color }}>
    <h4 style={summaryTitle}>{title}</h4>
    <p style={summaryValue}>{value}</p>
  </div>
);

// Styles
const pageContainer = {
  minHeight: "100vh",
  background: "linear-gradient(to bottom right, #f9fafb, #eef2ff)",
  padding: "30px 20px",
};

const headerBox = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#fff",
  padding: "16px 20px",
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  marginBottom: 20,
  flexWrap: "wrap",
};

const pageTitle = { fontSize: 22, fontWeight: 700, color: "#0f172a" };
const headerActions = { display: "flex", gap: 8 };

const btnExport = {
  background: "#0ea5e9",
  color: "#fff",
  padding: "8px 14px",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
};

const summaryBox = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
  marginBottom: 20,
};

const summaryCard = {
  borderRadius: 12,
  padding: "18px 16px",
  textAlign: "center",
  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
};
const summaryTitle = { margin: 0, fontSize: 14, fontWeight: 600, opacity: 0.8 };
const summaryValue = { margin: 0, fontSize: 22, fontWeight: 800 };

const filterBox = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
  background: "#fff",
  padding: 14,
  borderRadius: 12,
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  marginBottom: 20,
};

const inputStyle = {
  padding: "10px 14px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  fontSize: 15,
  flex: 1,
  minWidth: 200,
};
const dropdown = { ...inputStyle, cursor: "pointer" };

const btnFilter = {
  padding: "10px 20px",
  background: "#0ea5e9",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 2px 6px rgba(14,165,233,0.3)",
};

const cardStyle = {
  background: "#ffffff",
  padding: 20,
  borderRadius: 12,
  boxShadow: "0 2px 10px rgba(0,0,0,.05)",
  marginBottom: 16,
};

const th = { textAlign: "left", padding: 10, fontWeight: 700, borderBottom: "2px solid #e2e8f0" };
const thRight = { ...th, textAlign: "center" };
const thCenter = { ...th, textAlign: "center" };
const td = { padding: 10, borderBottom: "1px solid #f1f5f9", color: "#0f172a", textAlign: "left" };
const tdRight = { ...td, textAlign: "center" };
const tdCenter = { ...td, textAlign: "center" };
const rowAlt = { background: "#f9fafb" };
const rowDefault = { background: "#fff" };

const btnGhost = {
  padding: "8px 14px",
  background: "#0ea5e9",
  color: "#fff",
  borderRadius: 8,
  textDecoration: "none",
  fontWeight: 600,
  boxShadow: "0 1px 4px rgba(14,165,233,0.3)",
};

const paginationBox = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 12,
  marginTop: 24,
};

const btn = {
  padding: "8px 16px",
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
  fontWeight: 600,
  transition: "0.3s",
};

const pageIndicator = { fontSize: 15, color: "#475569" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 15 };

export default ProductBundleList;
